import type {CanvasPoint} from '@PosterWhiteboard/collaboration/poster-presence.types';

export interface ClientPoint {
  clientX: number;
  clientY: number;
}

/** The part of a DOMRect the pointer maths needs, so tests need no layout. */
export interface ClientRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CanvasSize {
  width: number;
  height: number;
}

const CANVAS_COORDINATE_PRECISION = 10;

/**
 * How far the pointer is from a rectangle in client pixels: zero inside it, the straight-line
 * distance to its nearest edge or corner outside. Picks which page a pointer off every page belongs to.
 */
export const distanceFromPointToRect = (pointer: ClientPoint, rect: ClientRect): number => {
  const horizontalGap = Math.max(rect.left - pointer.clientX, 0, pointer.clientX - (rect.left + rect.width));
  const verticalGap = Math.max(rect.top - pointer.clientY, 0, pointer.clientY - (rect.top + rect.height));
  return Math.hypot(horizontalGap, verticalGap);
};

/**
 * The pointer's position on a page in canvas pixels, through whatever CSS scale the page is shown
 * at. Outside the page the coordinates run past its edges (negative, or beyond the canvas size),
 * so a cursor in the margin around a page is still placed relative to it.
 */
export const toCanvasPointOnPage = (pointer: ClientPoint, pageRect: ClientRect, canvas: CanvasSize): CanvasPoint => ({
  x: roundCanvasCoordinate(((pointer.clientX - pageRect.left) / pageRect.width) * canvas.width),
  y: roundCanvasCoordinate(((pointer.clientY - pageRect.top) / pageRect.height) * canvas.height),
});

export const roundCanvasCoordinate = (value: number): number => Math.round(value * CANVAS_COORDINATE_PRECISION) / CANVAS_COORDINATE_PRECISION;
